// @givmo/design-system — RowActions (SaaS layer).
//
// A table-row action affordance: a kebab (⋮) `IconButton` trigger that opens the shared `Menu` with
// a list of actions. Destructive items render in danger red. Keeps per-row actions out of the row
// chrome (no always-visible trash icons) — they live behind one discoverable, accessible control.
//
// a11y: the trigger is an icon button with an `aria-label`; the menu is Base UI's accessible menu.
import type { ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { type TestIdProps } from '../lib/testid';
import { cn } from '../lib/utils';
import { Icon } from './icon';
import { IconButton } from './icon-button';
import { Button } from './button';
import { Menu } from './menu';

export interface RowAction {
  /** Visible label. */
  label: string;
  onSelect: () => void;
  /** Optional leading glyph. */
  icon?: ReactNode;
  /** Render in danger red (delete/remove). */
  destructive?: boolean;
  disabled?: boolean;
  /** Per-item testId (forwarded to the menu item). */
  testId?: string;
}

export interface RowActionsProps extends TestIdProps {
  actions: RowAction[];
  /** Accessible label for the trigger (e.g. "Actions for Ada Lovelace"). */
  label: string;
  /** Trigger size (default sm — fits a table row). */
  size?: 'sm' | 'md';
  disabled?: boolean;
  /** Styles the portaled menu without changing the defaults of other surfaces. */
  contentClassName?: string;
  /** A card's visible menu label; uses a full-width secondary button when provided. */
  triggerText?: string;
}

export function RowActions({
  actions,
  label,
  size = 'sm',
  disabled,
  testId,
  contentClassName,
  triggerText,
}: RowActionsProps) {
  return (
    <Menu.Root>
      <Menu.Trigger
        disabled={disabled}
        render={
          triggerText === undefined ? (
            <IconButton aria-label={label} tone="ghost" size={size} testId={testId} />
          ) : (
            <Button
              appearance="saas"
              aria-label={label}
              tone="secondary"
              size={size}
              fullWidth
              testId={testId}
            />
          )
        }
      >
        {triggerText ?? <Icon icon={MoreHorizontal} size={18} aria-hidden />}
      </Menu.Trigger>
      <Menu.Content
        saas
        side="bottom"
        align="end"
        className={cn('min-w-[176px]', contentClassName)}
      >
        {actions.map((action, i) => (
          <Menu.Item
            key={`${action.label}-${i}`}
            saas
            tone={action.destructive ? 'danger' : 'default'}
            onClick={action.onSelect}
            disabled={action.disabled}
            startIcon={action.icon}
            testId={action.testId}
          >
            {action.label}
          </Menu.Item>
        ))}
      </Menu.Content>
    </Menu.Root>
  );
}
